'use client';
import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { Device } from '@mentimeter/ragnar-device';
import NextImage from 'src/components/image/NextImage';
import type {
  IBlockBullets,
  IBlockHighlight,
} from 'types/generated/contentful';
import { Bullets, Highlight } from 'src/ui/blocks';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Ratio } from '@mentimeter/ragnar-ui/ratio';
import { Button, Link } from '../ui/actions';
import { Callout } from '../ui/Callout';
import { DecoratedImage } from '../ui/DecoratedImage';
import { Template } from '../ui/Template';
import { Bold, H2, H3, H4, Italic, P } from '../ui/typography';
import { Video } from '../ui/Video';
import { linkActionFields } from '../utils/linkActionFields';
import {
  getContentfulImageProps,
  getTemplatePageProps,
  getTemplateProps,
} from './utils/utils';
import TableRenderer, { type EmbeddedTableBlock } from './TableRenderer';

const options: Options = {
  renderMark: {
    [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
    [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
  },
  renderNode: {
    [BLOCKS.QUOTE]: (_, children) => {
      return (
        <Box
          as="blockquote"
          mt={['space1', 'space4']}
          mb={['space2', 'space8']}
          px={['space2', 'space8', 'space16']}
          alignItems="center"
          width="100%"
        >
          <P
            color="primary"
            fontSize={['125', '150']}
            textAlign="center"
            fontWeight="semiBold"
          >
            {/* @ts-expect-error-auto TS(2533) FIXME: Object is possibly 'null' or 'undefined'. */}
            {children[0].props.children[0]}
          </P>
        </Box>
      );
    },
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <P
        extend={() => ({
          ':empty': { display: 'none' },
        })}
      >
        {children}
      </P>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <H2 mt={['space4', 'space8']} mb={['space2', 'space4']}>
        {children}
      </H2>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <H3 mt={['space4', 'space8']} mb={['space2', 'space4']}>
        {children}
      </H3>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <H4 mt={['space4', 'space8']} mb={['space2', 'space4']}>
        {children}
      </H4>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <Box as="ol" pl="space8" mb={['space2', 'space8']}>
        {children}
      </Box>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <Box as="ul" pl="space8" mb={['space2', 'space8']}>
        {children}
      </Box>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <P as="li" mb="space0">
        {/* @ts-expect-error-auto TS(2533) FIXME: Object is possibly 'null' or 'undefined'. */}
        {children[0].props.children}
      </P>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri}>{children}</Link>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const id = node.data.target?.sys.contentType?.sys.id;
      switch (id) {
        case 'article': {
          const { fields } = node.data.target;
          return (
            <Box
              mb={['space2', 'space8']}
              width="100%"
              flexDirection={['column', 'row']}
              bg="neutralWeakest"
              p="space8"
            >
              <Ratio
                overflow="hidden"
                width={[1, 1 / 3]}
                mb={['space4', 'space0']}
              >
                {fields?.image?.fields?.image?.fields?.file && (
                  <NextImage
                    {...getContentfulImageProps(fields.image.fields)}
                    width={800}
                    height={600}
                  />
                )}
              </Ratio>
              <Device.Match greaterThan={1}>
                <Box width={[1, 2 / 3]} ml={['space0', 'space4']}>
                  <H2
                    lineHeight="heading"
                    color="text"
                    fontWeight="semiBold"
                    mb="space2"
                  >
                    {fields?.heading}
                  </H2>
                  {fields?.text && <Text lineHeight="body">{fields.text}</Text>}
                </Box>
                <Box
                  position="absolute"
                  bottom="0"
                  right="0"
                  m={['space2', 'space8']}
                >
                  {fields?.href && (
                    <Button variant="subtle" href={fields.href}>
                      Read more
                    </Button>
                  )}
                </Box>
              </Device.Match>
              <Device.Match lessThan={2}>
                <Box>
                  <Text
                    fontSize="125"
                    lineHeight="heading"
                    color="text"
                    fontWeight="semiBold"
                    as="h3"
                    mb="space4"
                  >
                    {fields?.heading}
                  </Text>
                  {fields?.href && (
                    <Button variant="subtle" href={fields.href}>
                      Read more
                    </Button>
                  )}
                </Box>
              </Device.Match>
            </Box>
          );
        }
        case 'blockBullets': {
          const { fields } = node.data.target as IBlockBullets;
          const bullets =
            fields?.bullets.map(({ fields }) => {
              return {
                bullet: fields?.bullet,
                heading: fields.heading,
                text: fields?.text,
                clickable: fields?.clickable,
                action: linkActionFields(fields?.action?.fields),
              };
            }) ?? [];
          return (
            <Bullets
              bullets={bullets}
              body={fields?.body}
              theme={
                fields?.mood?.fields
                  ? { colors: fields.mood.fields.colors }
                  : undefined
              }
              variant={fields?.variant}
              action={linkActionFields(fields?.action?.fields)}
            />
          );
        }

        case 'callout': {
          const { fields } = node.data.target;
          return (
            <Box mb={['space2', 'space8']} width="100%">
              <Callout
                body={fields?.body ? fields.body : undefined}
                action={linkActionFields(fields?.action?.fields)}
                theme={
                  fields?.mood?.fields
                    ? { colors: fields.mood.fields.colors }
                    : undefined
                }
              />
            </Box>
          );
        }

        case 'blockHighlight': {
          const { fields } = node.data.target as IBlockHighlight;
          return (
            <Highlight
              badge={fields?.badge}
              body={fields?.body}
              media={fields?.media}
              action={fields?.action}
              layout={fields?.layout}
              variant={fields?.variant}
              imageMaxWidth={fields.imageMaxWidth}
              mood={fields?.mood}
              imageVerticalAlignment={fields?.imageVerticalAlignment}
              useMinimumHeight={fields?.useMinimumHeight}
            />
          );
        }

        case 'image': {
          const { fields } = node.data.target;
          if (!fields?.image.fields) {
            return null;
          }
          const imageProps = {
            src: fields.image.fields.file.url,
            alt: fields.alt,
            decoration: fields.decoration,
            caption: fields.caption,
          };
          return (
            <Box mb={['space2', 'space8']} width="100%">
              <DecoratedImage width="100%" {...imageProps} />
            </Box>
          );
        }
        case 'video': {
          const { fields } = node.data.target;
          if (!fields?.url) {
            return null;
          }
          const videoProps = {
            preview: fields.preview?.fields?.file.url,
            url: fields.url,
            caption: fields.caption,
            title: fields.title,
          };
          return (
            <Box mb={['space2', 'space8']} width="100%">
              <Video {...videoProps} />
            </Box>
          );
        }
        case 'table': {
          const table = node as EmbeddedTableBlock;
          return <TableRenderer table={table} />;
        }
        case 'template': {
          const { fields } = node.data.target;
          if (!fields?.presentationId) {
            return null;
          }

          const templateProps = getTemplateProps(fields);

          return (
            <Box mb={['space2', 'space8']} width="100%">
              <Template {...templateProps} />
            </Box>
          );
        }
        case 'templatePage': {
          const { fields } = node.data.target;

          if (!fields?.template?.fields.presentationId) {
            return null;
          }

          const templatePageProps = getTemplatePageProps(fields);

          return (
            <Box mb={['space2', 'space8']} width="100%">
              <Template {...templatePageProps} />
            </Box>
          );
        }
        default:
          return null;
      }
    },
  },
};

interface Props {
  document: Document;
}

export function PostRenderer({ document }: Props): any {
  return documentToReactComponents(
    {
      ...document,
      content: document.content.map((x) => ({
        ...x,

        id: x.data.target?.sys.contentType?.sys.id,
      })),
    },
    options,
  );
}
